import { DomUtil, type LatLngBounds, type LatLngExpression } from "leaflet";
import {
    type RefObject,
    startTransition,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import type Light from "@/models/light";
import type { MarkerPosition } from "@/models/marker-position";
import type { MapHandle } from "@/types";
import { Map as MapUtil } from "@/utils";

export const legendZoomThreshold = 4.25;

const flagZoomHideThreshold = 7;
const flagZoomShowThreshold = 6;
const viewportDebounceDelay = 200;
const viewportPadding = 1;

interface Viewport {
    zoom: number;
    center: LatLngExpression;
    bounds: LatLngBounds;
}

/**
 * Props for the `useViewportCulling` hook.
 */
interface ViewportCullingProps {
    /**
     * The ref holding the imperative handle of the `Map` component.
     */
    mapRef: RefObject<MapHandle | null>;
}

/**
 * Tracks zoom and viewport bounds so off-screen fixtures can be culled and the
 * legend and flag overlays toggled at their zoom thresholds.
 *
 * @param params - The map handle ref.
 * @returns The viewport state, the viewport change handler, and culling predicates.
 */
export const useViewportCulling = ({ mapRef }: ViewportCullingProps) => {
    const [showFlag, setShowFlag] = useState(false);
    const [legendsVisible, setLegendsVisible] = useState(true);

    const [viewportBounds, setViewportBounds] = useState<LatLngBounds | null>(
        null,
    );

    const viewportBoundsRef = useRef<LatLngBounds | null>(null);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        [],
    );

    const setLegendVisibility = useCallback(
        (visible: boolean) => {
            if (!mapRef.current) return;

            const { map } = mapRef.current;

            DomUtil[`${visible ? "remove" : "add"}Class`](
                map.getPane("markerPane"),
                "hidden",
            );

            setLegendsVisible(visible);
        },
        [mapRef],
    );

    const onUpdateViewport = useCallback(
        (viewport: Viewport) => {
            MapUtil.setViewport(viewport);

            if (legendsVisible && viewport.zoom < legendZoomThreshold) {
                setLegendVisibility(false);
            } else if (
                !legendsVisible &&
                viewport.zoom >= legendZoomThreshold
            ) {
                setLegendVisibility(true);
            }

            if (showFlag && viewport.zoom < flagZoomHideThreshold) {
                setShowFlag(false);
            } else if (!showFlag && viewport.zoom > flagZoomShowThreshold) {
                setShowFlag(true);
            }

            if (!viewport.bounds) return;

            // Only recalculate culling when the visible area has moved outside the
            // current padded region. This prevents a feedback loop where
            // mount/unmount → canvas redraw → moveend → repeat.
            const needsUpdate =
                !viewportBoundsRef.current ||
                !viewportBoundsRef.current.contains(viewport.bounds);

            if (!needsUpdate) return;

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
                const padded = viewport.bounds.pad(viewportPadding);
                viewportBoundsRef.current = padded;

                startTransition(() => {
                    setViewportBounds(padded);
                });
            }, viewportDebounceDelay);
        },
        [showFlag, legendsVisible, setLegendVisibility],
    );

    const isLightInViewport = useCallback(
        (light: Light) =>
            !viewportBounds || viewportBounds.overlaps(light.bounds),
        [viewportBounds],
    );

    const isMarkerInViewport = useCallback(
        (marker: MarkerPosition) =>
            !viewportBounds || viewportBounds.contains([marker.y, marker.x]),
        [viewportBounds],
    );

    return {
        isLightInViewport,
        isMarkerInViewport,
        legendsVisible,
        onUpdateViewport,
        setLegendVisibility,
        showFlag,
    };
};
